/**
 * Unified Data Transformation Utilities
 * Eliminates duplicated data transformation logic across shift management services
 * Provides consistent data normalization and conversion patterns
 */

import { parseISO, isValid } from 'date-fns';
import { DayShift, Shift, Employee } from '@/types';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

// Raw database shift interface (from Supabase)
export interface RawShift {
  id: string;
  title?: string | null;
  start_time: string;  // ISO timestamp
  end_time: string;    // ISO timestamp
  employee_id?: string | null;
  business_id: string;
  notes?: string | null;
  status?: string | null;
  created_at?: string;
  updated_at?: string;
  // Joined fields
  employees?: {
    id: string;
    name: string;
    email?: string;
  } | null;
}

// Raw shift request interface
export interface RawShiftRequest {
  id: string;
  shift_id: string;
  employee_id: string;
  status?: string | null;
  employee_notes?: string | null;
  manager_notes?: string | null;
  requested_at?: string;
  reviewed_at?: string | null;
  created_at?: string;
  // Joined fields
  shifts?: RawShift | null;
  employees?: {
    id: string;
    name: string;
    email?: string;
  } | null;
}

// Transformation result interfaces
export interface TransformedShift extends DayShift {
  rawData?: RawShift; // Keep reference to original data
}

export interface TransformedShiftRequest {
  id: string;
  shiftId: string;
  employeeId: string;
  status: string | null;
  employeeNotes: string | null;
  managerNotes: string | null;
  requestedAt: Date;
  reviewedAt: Date | null;
  createdAt: Date;
  // Related data
  shift?: TransformedShift | null;
  employee?: {
    id: string;
    name: string;
    email?: string;
  } | null;
  rawData?: RawShiftRequest; // Keep reference to original data
}

// Transformation options
export interface TransformationOptions {
  preserveRawData?: boolean;     // Keep reference to original data
  validateDates?: boolean;       // Validate date parsing
  includeMetadata?: boolean;     // Include created/updated timestamps
  calculateDuration?: boolean;   // Calculate shift duration
  normalizeEmployeeData?: boolean; // Normalize employee information
}

// Default transformation options
const DEFAULT_OPTIONS: TransformationOptions = {
  preserveRawData: false,
  validateDates: true,
  includeMetadata: true,
  calculateDuration: true,
  normalizeEmployeeData: true
};

/**
 * Core data transformation service
 * Consolidates data conversion logic from multiple services
 */
class UnifiedDataTransformationService {
  private static instance: UnifiedDataTransformationService;

  static getInstance(): UnifiedDataTransformationService {
    if (!this.instance) {
      this.instance = new UnifiedDataTransformationService();
    }
    return this.instance;
  }

  /**
   * Transform raw database shifts to DayShift objects
   * Replaces duplicated transformation logic across components
   */
  transformShifts(
    rawShifts: RawShift[],
    employees: Employee[] = [],
    options: TransformationOptions = {}
  ): TransformedShift[] {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    return rawShifts
      .map(rawShift => this.transformSingleShift(rawShift, employees, opts))
      .filter((shift): shift is TransformedShift => shift !== null);
  }

  /**
   * Transform a single raw shift
   */
  private transformSingleShift(
    rawShift: RawShift,
    employees: Employee[],
    options: TransformationOptions
  ): TransformedShift | null {
    try {
      // Parse and validate dates
      const startTime = parseISO(rawShift.start_time);
      const endTime = parseISO(rawShift.end_time);

      if (options.validateDates && (!isValid(startTime) || !isValid(endTime))) {
        console.warn(`Invalid dates in shift ${rawShift.id}:`, {
          start_time: rawShift.start_time,
          end_time: rawShift.end_time
        });
        return null;
      }

      // Find employee information
      let employeeData: { id: string; name: string; email?: string } | null = null;
      let employeeName: string | undefined = undefined;

      if (rawShift.employee_id) {
        // Try joined employee data first
        if (rawShift.employees) {
          employeeData = rawShift.employees;
          employeeName = rawShift.employees.name;
        } else if (options.normalizeEmployeeData && employees.length > 0) {
          // Fallback to provided employees array
          const employee = employees.find(e => e.id === rawShift.employee_id);
          if (employee) {
            employeeData = {
              id: employee.id,
              name: employee.name,
              email: employee.email || undefined
            };
            employeeName = employee.name;
          }
        }
      }

      // Calculate duration if requested
      let durationMinutes: number | undefined;
      if (options.calculateDuration) {
        durationMinutes = Math.max(0, 
          (endTime.getTime() - startTime.getTime()) / (1000 * 60)
        );
      }

      // Build transformed shift
      const transformed: TransformedShift = {
        id: rawShift.id,
        title: rawShift.title || undefined,
        startTime,
        endTime,
        employeeId: rawShift.employee_id || undefined,
        employeeName,
        business_id: rawShift.business_id,
        notes: rawShift.notes || undefined,
        status: rawShift.status as ShiftStatus | undefined,
        requestCount: 0, // Will be populated by caller if needed
        
        // Additional computed fields
        ...(durationMinutes !== undefined && { durationMinutes }),
        ...(employeeData && { employeeData }),
        
        // Metadata
        ...(options.includeMetadata && {
          createdAt: rawShift.created_at ? parseISO(rawShift.created_at) : undefined,
          updatedAt: rawShift.updated_at ? parseISO(rawShift.updated_at) : undefined
        }),
        
        // Raw data reference
        ...(options.preserveRawData && { rawData: rawShift })
      };

      return transformed;
      
    } catch (error) {
      console.error(`Error transforming shift ${rawShift.id}:`, error);
      return null;
    }
  }

  /**
   * Transform raw shift requests to normalized objects
   * Consolidates request data transformation
   */
  transformShiftRequests(
    rawRequests: RawShiftRequest[],
    options: TransformationOptions = {}
  ): TransformedShiftRequest[] {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    return rawRequests
      .map(rawRequest => this.transformSingleShiftRequest(rawRequest, opts))
      .filter((request): request is TransformedShiftRequest => request !== null);
  }

  /**
   * Transform a single raw shift request
   */
  private transformSingleShiftRequest(
    rawRequest: RawShiftRequest,
    options: TransformationOptions
  ): TransformedShiftRequest | null {
    try {
      // Parse and validate dates
      const requestedAt = parseISO(rawRequest.requested_at || rawRequest.created_at);
      const reviewedAt = rawRequest.reviewed_at ? parseISO(rawRequest.reviewed_at) : null;
      const createdAt = parseISO(rawRequest.created_at);

      if (options.validateDates && !isValid(requestedAt)) {
        console.warn(`Invalid requested_at date in request ${rawRequest.id}:`, 
          rawRequest.requested_at || rawRequest.created_at);
        return null;
      }

      // Transform associated shift if present
      let transformedShift: TransformedShift | null = null;
      if (rawRequest.shifts && options.normalizeEmployeeData) {
        transformedShift = this.transformSingleShift(rawRequest.shifts, [], options);
      }

      // Build transformed request
      const transformed: TransformedShiftRequest = {
        id: rawRequest.id,
        shiftId: rawRequest.shift_id,
        employeeId: rawRequest.employee_id,
        status: rawRequest.status,
        employeeNotes: rawRequest.employee_notes,
        managerNotes: rawRequest.manager_notes,
        requestedAt,
        reviewedAt,
        createdAt,
        
        // Related data
        ...(transformedShift && { shift: transformedShift }),
        ...(rawRequest.employees && { employee: rawRequest.employees }),
        
        // Raw data reference
        ...(options.preserveRawData && { rawData: rawRequest })
      };

      return transformed;
      
    } catch (error) {
      console.error(`Error transforming shift request ${rawRequest.id}:`, error);
      return null;
    }
  }

  /**
   * Normalize employee data across different sources
   * Handles inconsistent employee data structures
   */
  normalizeEmployeeData(
    employees: (Employee | { id: string; name: string; email?: string })[]
  ): Array<{ id: string; name: string; email?: string; isActive?: boolean }> {
    return employees.map(emp => ({
      id: emp.id,
      name: emp.name,
      email: emp.email,
      isActive: 'is_active' in emp ? emp.is_active : true
    }));
  }

  /**
   * Group shifts by date for calendar display
   * Replaces duplicated grouping logic
   */
  groupShiftsByDate(
    shifts: TransformedShift[],
    dateFormat: string = 'yyyy-MM-dd'
  ): Map<string, TransformedShift[]> {
    const grouped = new Map<string, TransformedShift[]>();
    
    shifts.forEach(shift => {
      // Use start date for grouping
      const dateKey = shift.startTime.toISOString().split('T')[0]; // Simple YYYY-MM-DD
      
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      
      grouped.get(dateKey)!.push(shift);
    });
    
    return grouped;
  }

  /**
   * Enrich shifts with additional data
   * Adds computed fields and related data
   */
  enrichShifts(
    shifts: TransformedShift[],
    enrichmentData: {
      requestCounts?: Map<string, number>;
      timeLogsMap?: Map<string, { arrival?: string; departure?: string }>;
      employees?: Employee[];
    }
  ): TransformedShift[] {
    const { requestCounts, timeLogsMap, employees } = enrichmentData;
    
    return shifts.map(shift => ({
      ...shift,
      
      // Add request count if available
      ...(requestCounts?.has(shift.id) && { 
        requestCount: requestCounts.get(shift.id)! 
      }),
      
      // Add time log data if available
      ...(timeLogsMap?.has(shift.id) && {
        actualArrivalTime: timeLogsMap.get(shift.id)!.arrival,
        actualDepartureTime: timeLogsMap.get(shift.id)!.departure
      }),
      
      // Update employee name if not present
      ...(!shift.employeeName && shift.employeeId && employees && {
        employeeName: employees.find(e => e.id === shift.employeeId)?.name
      })
    }));
  }

  /**
   * Convert DayShift back to database format
   * Used for API updates
   */
  shiftToRawFormat(shift: DayShift): Partial<RawShift> {
    return {
      id: shift.id,
      title: shift.title || null,
      start_time: shift.startTime.toISOString(),
      end_time: shift.endTime.toISOString(),
      employee_id: shift.employeeId || null,
      business_id: shift.business_id,
      notes: shift.notes || null,
      status: shift.status || null
    };
  }

  /**
   * Batch transform operations for performance
   * Handles large datasets efficiently
   */
  batchTransform(
    operations: Array<{
      type: 'shifts' | 'requests';
      data: RawShift[] | RawShiftRequest[];
      options?: TransformationOptions;
      employees?: Employee[];
    }>
  ): {
    shifts: TransformedShift[];
    requests: TransformedShiftRequest[];
    errors: Array<{ operation: number; error: string }>;
  } {
    const results = {
      shifts: [] as TransformedShift[],
      requests: [] as TransformedShiftRequest[],
      errors: [] as Array<{ operation: number; error: string }>
    };
    
    operations.forEach((op, index) => {
      try {
        if (op.type === 'shifts') {
          const transformed = this.transformShifts(
            op.data as RawShift[], 
            op.employees || [], 
            op.options
          );
          results.shifts.push(...transformed);
        } else {
          const transformed = this.transformShiftRequests(
            op.data as RawShiftRequest[], 
            op.options
          );
          results.requests.push(...transformed);
        }
      } catch (error) {
        results.errors.push({
          operation: index,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
    
    return results;
  }
}

// Export singleton instance
export const unifiedDataTransformationService = UnifiedDataTransformationService.getInstance();

// Convenience wrapper functions
export const transformShifts = (
  rawShifts: RawShift[],
  employees?: Employee[],
  options?: TransformationOptions
) => unifiedDataTransformationService.transformShifts(rawShifts, employees, options);

export const transformShiftRequests = (
  rawRequests: RawShiftRequest[],
  options?: TransformationOptions
) => unifiedDataTransformationService.transformShiftRequests(rawRequests, options);

export const groupShiftsByDate = (
  shifts: TransformedShift[],
  dateFormat?: string
) => unifiedDataTransformationService.groupShiftsByDate(shifts, dateFormat);

export const enrichShifts = (
  shifts: TransformedShift[],
  enrichmentData: Parameters<typeof unifiedDataTransformationService.enrichShifts>[1]
) => unifiedDataTransformationService.enrichShifts(shifts, enrichmentData);

export const normalizeEmployeeData = (
  employees: Parameters<typeof unifiedDataTransformationService.normalizeEmployeeData>[0]
) => unifiedDataTransformationService.normalizeEmployeeData(employees);

export const shiftToRawFormat = (shift: DayShift) => 
  unifiedDataTransformationService.shiftToRawFormat(shift);

// Export service and types
export { UnifiedDataTransformationService };
export type { 
  RawShift, 
  RawShiftRequest, 
  TransformedShift, 
  TransformedShiftRequest, 
  TransformationOptions 
};